import { defineStore } from 'pinia';
import api from '../api';

export interface RileyUser {
  id: number;
  name: string;
  email: string;
  role: 'admin' | 'user';
  customer_id: number;
  customer_name: string;
  email_verified?: boolean;
}

export const useAuthStore = defineStore('authStore', {
  state: () => ({
    isAuthenticated: false,
    token: '',
    user: null as RileyUser | null,
  }),
  getters: {
    getAuthStatus: (state) => state.isAuthenticated,
    getToken: (state) => state.token,
    getUser: (state) => state.user,
    isAdmin: (state) => state.user?.role === 'admin',
    isEmailVerified: (state) => state.user?.email_verified !== false,
  },
  actions: {
    setSession(token: string, user: RileyUser) {
      this.token = token;
      this.user  = user;
      this.isAuthenticated = true;
      // Keep token in memory only — not localStorage (HttpOnly cookie is the durable credential)
      (window as any).__rileyToken = token;
      // Keep non-sensitive user profile in localStorage for instant UI restore on refresh
      localStorage.setItem('_riley_user', JSON.stringify(user));
      // Remove legacy token from localStorage if present from a previous session
      localStorage.removeItem('_riley');
    },
    clearSession() {
      this.token = '';
      this.user  = null;
      this.isAuthenticated = false;
      (window as any).__rileyToken = null;
      localStorage.removeItem('_riley');
      localStorage.removeItem('_riley_user');
    },
    restoreSession() {
      // Restore user object from localStorage for instant UI (no flash)
      const user = localStorage.getItem('_riley_user');
      if (user) {
        try {
          this.user = JSON.parse(user);
          this.isAuthenticated = true;
        } catch {}
      }
    },
    async init() {
      // On page load: call /session to verify the HttpOnly cookie is still valid
      // and get a fresh token + user object.
      try {
        const res = await api.get('customer/auth/session');
        const user = res.data?.data?.user;
        if (user) {
          this.user = user;
          this.isAuthenticated = true;
          localStorage.setItem('_riley_user', JSON.stringify(user));
        }
      } catch {
        this.clearSession();
      }
    },
    async pollProfile() {
      if (!this.isAuthenticated) return;
      try {
        const res = await api.get('customer/profile');
        const profile = res.data?.data?.profile;
        if (profile) {
          // Merge (don't replace) so fields the /profile endpoint omits
          // (e.g. customer_id, customer_name) are preserved.
          this.user = { ...this.user, ...profile } as RileyUser;
          localStorage.setItem('_riley_user', JSON.stringify(this.user));
        }
      } catch (err: any) {
        if (err.response?.status === 401) {
          this.clearSession();
          if (window.location.pathname !== '/login') window.location.href = '/login';
        }
      }
    },
    async resendVerification() {
      await api.post('customer/auth/resend-verification');
    },
    // Legacy compat helpers used by older components
    setAuthStatus(val: boolean) { this.isAuthenticated = val; },
    setToken(val: string) { this.token = val; },
  },
});
